import { customElement, state } from "lit/decorators.js";
import { html, nothing, type PropertyValues } from "lit";
import { FinalFormController, zodValidator } from "../controllers/final-form.controller";
import { alertController, toastController } from "@ionic/core";
import { consume } from "@lit/context";
import { authContext, type AuthContext } from "../context/auth.context";
import { Task } from "@lit/task";
import { choose } from "lit/directives/choose.js";
import { navigatorContext, NavigatorController } from "../controllers/navigator.controller";
import { Page, required } from "../components/component";
import { reportsStore, type ObservableReport } from "../stores/reports.store";
import { UserShareStore } from "../stores/userShare.store";
import { groupStore } from "../stores/groups.store";
import { UpdateReportSchema, type UpdateReport } from "../repository/report/report";
import { when } from "lit/directives/when.js";
import { map } from "lit/directives/map.js";
import { localized, msg, str } from "@lit/localize";
import { ApiError } from "../error";

@customElement("modal-edit-report-library")
@localized()
export class ModalEditReportLibrary extends Page {
  @consume({ context: navigatorContext }) navigator!: NavigatorController;
  @consume({ context: authContext }) private auth!: AuthContext;

  @state() users: UserShareStore["users"] = [];
  @required() report!: ObservableReport;

  shareStore = new UserShareStore();

  connectedCallback(): void {
    super.connectedCallback();
    this.shareStore.set(
      this.report.sharedPersonal.map((share) => ({
        user: share.user,
        access: share.access,
        action: "update",
      })),
    );

    this.users = this.shareStore.updatedUsers.slice(0, 2);
  }

  protected firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);

    //so that new users stay in the same order in the list depending when they were added
    this.shareStore.on("create", () => {
      this.users.push(this.shareStore.newUsers[this.shareStore.newUsers.length - 1]!);
      this.requestUpdate();
    });

    this.shareStore.on("delete", () => {
      this.users = this.users.filter((user) => user.action !== "delete");
      // if its a new user that was deleted, remove it
      this.users.map((user) => {
        if (user.action === "create" && !this.shareStore.newUsers.includes(user)) {
          this.users = this.users.filter((u) => u !== user);
        }
      });
      this.requestUpdate();
    });
  }

  groups = new Task(this, {
    task: async () => {
      if (this.report.isSharedFromGroup() && this.report.isOwner(this.auth.user)) {
        return groupStore.loadGroupsByOwner(this.auth.user.id);
      }
      return [];
    },
    args: () => [],
  });

  #controller = new FinalFormController<UpdateReport>(this, {
    validate: zodValidator(UpdateReportSchema()),
    onSubmit: async (values) => {
      try {
        await reportsStore.updateReport(this.report, {
          name: values.name,
        });

        if (this.report.isSharedFromGroup()) {
          if (values.group) await reportsStore.shareWithGroup(this.report, values.group);
        } else {
          await reportsStore.shareWithUsers(this.report, this.shareStore.users);
        }
        this.navigator.close();
      } catch (error) {
        if (error instanceof ApiError) {
          const alert = await alertController.create({
            header: msg("Erro"),
            message: error.message,
            buttons: [msg("Ok")],
          });
          await alert.present();
        }
      }
    },
  });

  async deleteReport() {
    const alert = await alertController.create({
      header: msg("Apagar biblioteca"),
      message: msg("Tem a certeza que deseja apagar esta biblioteca? A ação não é reversível."),
      buttons: [
        {
          text: msg("Cancelar"),
          role: "cancel",
        },
        {
          text: msg("Apagar"),
          role: "destructive",
          handler: async () => {
            try {
              await reportsStore.deleteReport(this.report).then(async () => {
                const toast = await toastController.create({
                  message: msg("Biblioteca apagada com sucesso"),
                  color: "primary",
                  icon: "/assets/icons/info.svg",
                  duration: 2000,
                });
                await toast.present();
              });
              this.navigator.close();
            } catch (error) {
              if (error instanceof ApiError) {
                const alert = await alertController.create({
                  header: msg("Erro"),
                  message: error.message,
                  buttons: [msg("Ok")],
                });
                await alert.present();
              }
            }
          },
        },
      ],
    });
    await alert.present();
  }

  render() {
    const { form, submit, register, renderError } = this.#controller;
    const formState = form.getState();

    const currentUser = this.auth.user;

    return html`
      <!-- HEADER -->
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start">
            <div class="flex items-center">
              <ion-button
                @click=${this.navigator.goBack}
                style="--padding-inline-start: 0px; --padding-start: 0px; margin-inline-start: 0px; margin-start: 0px;"
                fill="clear"
                class="font-semibold">
                ${msg("Cancelar")}
              </ion-button>
            </div>
          </ion-buttons>
          <ion-title>${this.report.name}</ion-title>
          ${when(
            // !(
            //   (this.report.isSharedFromGroup() && !this.report.isOwner(currentUser)) ||
            //   this.report.isShared(currentUser)
            // ),
            this.report.hasPrivileges(currentUser),
            () => html`
              <ion-buttons slot="end">
                <div class="flex items-center">
                  <ion-button
                    @click=${() => this.deleteReport()}
                    color="danger"
                    style="--padding-inline-start: 0px; --padding-start: 0px; margin-inline-start: 0px; margin-start: 0px;"
                    fill="clear"
                    class="font-semibold">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      class="mr-1">
                      <path
                        d="M5.33333 8H10.6667M8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14Z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                    ${msg("Apagar")}
                  </ion-button>
                </div>
              </ion-buttons>
            `,
          )}
        </ion-toolbar>
      </ion-header>

      <ion-content fullscreen>
        <form class="mt-4" id="form-library-edit" @submit=${submit}>
          <div>
            <span class="font-semibold font-display">${msg("Nome")}</span>
            <ion-item class="has-input mt-1">
              <ion-input
                autocapitalize="on"
                ?disabled=${!this.report.hasPrivileges(currentUser)}
                type="text"
                placeholder=${msg("Nome da Biblioteca")}
                ${register("name", { initialValue: this.report.name })}></ion-input>
            </ion-item>
            ${renderError("name")}
          </div>

          ${this.report.isSharedFromGroup() && !this.report.isOwner(currentUser)
            ? html`
                <ion-item class="w-full h-[139px] rounded-md no-p no-inner-p mt-4">
                  <div
                    class="w-full h-[139px] bg-accent-7 rounded-md col justify-center items-center space-y-1 text-accent-1">
                    <span class="font-display font-semibold text-center" style="max-inline-size: 20ch">
                      <span class="font-medium">${msg("Esta biblioteca é partilhada pelo grupo")}</span>"${this.report
                        .sharedGroup?.group.name}"
                    </span>
                  </div>
                </ion-item>
              `
            : this.report.isSharedFromGroup() && this.report.isOwner(currentUser)
            ? this.groups.render({
                complete: (groups) => html`
                  <div class="mt-6 space-y-2">
                    <span class="font-semibold font-display">${msg("Grupos")}</span>
                    <ion-list>
                      <ion-radio-group
                        allow-empty-selection
                        ${register("group", {
                          initialValue: this.report.sharedGroup?.group.id,
                        })}
                        value=${this.report.sharedGroup?.group.id}>
                        ${map(groups, (group) => {
                          if (group.isOwner(this.auth.user)) {
                            return html`
                              <ion-item class="text-accent-5 no-p no-inner-p" style="--min-height: 48px;">
                                <ion-radio value=${group.id} class="no-m-inline my-2">${group.name}</ion-radio>
                              </ion-item>
                            `;
                          }
                          return nothing;
                        })}
                      </ion-radio-group>
                    </ion-list>
                    ${renderError("group")}
                  </div>
                `,
              })
            : html`
                <div class="mt-6 space-y-2">
                  <div class="flex justify-between items-center">
                    <span class="font-semibold font-display">${msg("Profissionais")}</span>
                    ${when(
                      this.report.hasPrivileges(this.auth.user),
                      () => html`
                        <ion-button
                          class="h-7 text-sm"
                          style="--padding-start: 8px; --padding-end: 8px;"
                          color="secondary"
                          shape="round"
                          @click=${() => {
                            this.navigator.push("modal-share-add", {
                              shareStore: this.shareStore,
                            });
                          }}>
                          <svg
                            class="mr-1"
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none">
                            <path
                              d="M4 8H8M8 8H12M8 8V12M8 8V4"
                              stroke="#111111"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round" />
                          </svg>
                          ${msg("Adicionar")}
                        </ion-button>
                      `,
                    )}
                  </div>
                  <ion-list class="space-y-2.5 no-p" lines="none">
                    <ion-item class="no-p">
                      <div class="flex items-center space-x-2">
                        <xt-avatar src=${this.report.owner.avatar} name=${this.report.owner.name}></xt-avatar>
                        <div class="col items-start">
                          <p class="font-semibold">${this.report.owner.name}</p>
                          <span class="text-danger">${msg("Criador")}</span>
                        </div>
                      </div>
                    </ion-item>

                    ${this.users.map(
                      (share) => html`
                        <ion-item
                          class="no-p"
                          @click=${() => {
                            if (this.report.hasPrivileges(this.auth.user))
                              this.navigator.push("modal-share-edit", {
                                share: share,
                                shareStore: this.shareStore,
                              });
                          }}>
                          <div class="flex items-center space-x-2">
                            <xt-avatar src=${share.user.avatar} name=${share.user.name}></xt-avatar>
                            <div class="col items-start">
                              <p class="font-semibold">${share.user.name}</p>
                              ${choose(share.access.role, [
                                ["admin", () => html` <span class="text-successDark">${msg("Administrador")}</span> `],
                                ["editor", () => html` <span class="text-warning">${msg("Editor")}</span> `],
                                ["viewer", () => html` <span class="text-accent-2">${msg("Visualizador")}</span> `],
                              ])}
                            </div>
                          </div>
                        </ion-item>
                      `,
                    )}
                    ${when(
                      this.shareStore.updatedUsers?.length > 2 &&
                        this.users.filter((user) => user.action === "update").length === 2,
                      () => {
                        return html`
                          <ion-button
                            class="mt-3"
                            size="small"
                            color="secondary"
                            expand="block"
                            shape="round"
                            @click=${() => (
                              this.users.push(...this.shareStore.updatedUsers.slice(2)), this.requestUpdate()
                            )}
                            >${msg(str`Ver Todos (${this.shareStore.updatedUsers.length + 1})`)}
                          </ion-button>
                        `;
                      },
                    )}
                  </ion-list>
                </div>
              `}
        </form>
      </ion-content>

      <ion-footer>
        <ion-toolbar>
          ${when(
            this.report.hasPrivileges(currentUser),
            () => html`
              <ion-button
                form="form-library-edit"
                type="submit"
                expand="block"
                shape="round"
                ?disabled=${formState.submitting}>
                ${msg("Guardar Alterações")}
              </ion-button>
            `,
          )}
        </ion-toolbar>
      </ion-footer>
    `;
  }
}
